import {FirestoreTraceProjectRef, TraceProjectRef} from "@buildwithflux/constants";

import {getCanonicalProjectUrlForSearch} from "./document";
import {DocumentDataForUrlRouting} from "./models";

/**
 * Origin used for the stored canonical project URL. Trace records are
 * written server-side (no `location`), so the production origin is pinned
 * explicitly instead of relying on `toAbsoluteUrl`'s fallback.
 */
const CANONICAL_PROJECT_ORIGIN = "https://www.flux.ai";

/**
 * Builds the denormalized {@link TraceProjectRef} stored on agent trace
 * records, reusing the canonical slug URL routing (which appends the
 * `~shortId` suffix when the document has one).
 *
 * Returns undefined when the document cannot be canonically routed by slug
 * (missing slug/owner_handle, local component, or enterprise document
 * without a resolved enterprise handle) — callers should then fall back to
 * the bare project UID.
 */
export function buildTraceProjectRef(document: DocumentDataForUrlRouting): TraceProjectRef | undefined {
    const relativeUrl = getCanonicalProjectUrlForSearch(document);
    if (!relativeUrl || !document.uid || !document.slug || !document.owner_handle) {
        return undefined;
    }
    const shortId = typeof document.short_id === "string" ? document.short_id : document.short_id?.value;
    return {
        uid: document.uid,
        ownerHandle: document.owner_handle,
        slug: document.slug,
        ...(shortId ? {shortId} : {}),
        url: new URL(relativeUrl, CANONICAL_PROJECT_ORIGIN).toString(),
    };
}

/**
 * Serializes a {@link TraceProjectRef} to the snake_cased `project_ref` wire
 * shape stored on `agent_trace_runs` Firestore records.
 */
export function toFirestoreTraceProjectRef(ref: TraceProjectRef): FirestoreTraceProjectRef {
    return {
        uid: ref.uid,
        owner_handle: ref.ownerHandle,
        slug: ref.slug,
        ...(ref.shortId !== undefined ? {short_id: ref.shortId} : {}),
        url: ref.url,
    };
}

/**
 * Parses an untrusted Firestore `project_ref` field back into a
 * {@link TraceProjectRef}. Returns undefined when the value is missing or
 * malformed (all required fields must be strings).
 */
export function fromFirestoreTraceProjectRef(value: unknown): TraceProjectRef | undefined {
    if (typeof value !== "object" || value === null) return undefined;
    const {uid, owner_handle: ownerHandle, slug, url, short_id: shortId} = value as {[key: string]: unknown};
    if (
        typeof uid !== "string" ||
        typeof ownerHandle !== "string" ||
        typeof slug !== "string" ||
        typeof url !== "string"
    ) {
        return undefined;
    }
    return {uid, ownerHandle, slug, ...(typeof shortId === "string" ? {shortId} : {}), url};
}
